/**
 * Updates the main-product-individual-T1 Add to Cart line total (variant price × quantity).
 * Listens for quantity changes and DOM updates after variant-driven section refresh.
 */
if (!customElements.get('individual-atc-total')) {
  class IndividualAtcTotal extends HTMLElement {

    /** @type {MutationObserver | null} */
    addToCartObserver = null;

    /** @type {AbortController | null} */
    quantityAbort = null;

    connectedCallback() {
      this.sync = this.sync.bind(this);
      this.onQuantityClick = this.onQuantityClick.bind(this);
      this.onAddToCartMutations = this.onAddToCartMutations.bind(this);

      const productInfo = this.closest('product-info');
      if (!productInfo) return;

      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      const addToCartContainer = productInfo.querySelector(`#add-to-cart-container-${sectionId}`);
      if (addToCartContainer) {
        this.addToCartObserver = new MutationObserver(this.onAddToCartMutations);
        this.addToCartObserver.observe(addToCartContainer, { childList: true, subtree: true });
      }

      this.bindQuantityListeners(productInfo, sectionId);
      this.sync();
    }

    disconnectedCallback() {
      this.addToCartObserver?.disconnect();
      this.addToCartObserver = null;
      this.quantityAbort?.abort();
      this.quantityAbort = null;
    }

    /**
     * @param {MutationRecord[]} _records
     * @param {MutationObserver} _observer
     * @returns {void}
     */
    onAddToCartMutations(_records, _observer) {
      requestAnimationFrame(() => {
        this.sync();
      });
    }

    /**
     * @param {HTMLElement} productInfo
     * @param {string} sectionId
     * @returns {void}
     */
    bindQuantityListeners(productInfo, sectionId) {
      this.quantityAbort?.abort();
      this.quantityAbort = new AbortController();
      const signal = this.quantityAbort.signal;

      const quantityInput = productInfo.querySelector(`#Quantity-${sectionId}`);
      if (quantityInput) {
        quantityInput.addEventListener('input', this.sync, { signal });
        quantityInput.addEventListener('change', this.sync, { signal });
      }

      const quantitySelector = productInfo.querySelector('quantity-selector');
      if (quantitySelector) {
        quantitySelector.addEventListener('click', this.onQuantityClick, { signal });
      }
    }

    /**
     * @param {MouseEvent} e
     * @returns {void}
     */
    onQuantityClick(e) {
      const btn = e.target?.closest?.('button[name="plus"], button[name="minus"]');
      if (!btn) return;

      requestAnimationFrame(() => {
        this.sync();
      });
    }

    /**
     * @param {HTMLElement} productInfo
     * @returns {number | null} Price in cents
     */
    getVariantPriceCents(productInfo) {
      const variantScript = productInfo.querySelector('variant-selector script[data-selected-variant]');
      if (variantScript?.textContent) {
        try {
          const variant = JSON.parse(variantScript.textContent);
          return this.normalizePriceToCents(variant?.price);
        } catch {
          return null;
        }
      }

      const productScript = productInfo.querySelector('script[data-product]');
      const idInput = productInfo.querySelector('input[name="id"][form]') || productInfo.querySelector('form input[name="id"]');
      if (!productScript?.textContent || !idInput?.value) return null;

      try {
        const product = JSON.parse(productScript.textContent);
        const variantId = Number.parseInt(String(idInput.value), 10);
        const variants = product?.variants;
        if (!Array.isArray(variants)) return null;

        for (const v of variants) {
          const vid = Number.parseInt(String(v.id), 10);
          if (vid === variantId) {
            return this.normalizePriceToCents(v.price);
          }
        }
      } catch {
        return null;
      }

      return null;
    }

    /**
     * Shopify JSON may expose price as integer cents or decimal string.
     * @param {unknown} raw
     * @returns {number | null}
     */
    normalizePriceToCents(raw) {
      if (raw == null) return null;
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        if (Number.isInteger(raw)) return raw;
        return Math.round(raw * 100);
      }
      if (typeof raw === 'string') {
        const n = Number.parseFloat(raw);
        if (!Number.isFinite(n)) return null;
        return Math.round(n * 100);
      }
      return null;
    }

    /**
     * @param {HTMLElement} productInfo
     * @param {string} sectionId
     * @returns {number}
     */
    getQuantity(productInfo, sectionId) {
      const input = productInfo.querySelector(`#Quantity-${sectionId}`);
      if (!(input instanceof HTMLInputElement)) return 1;

      let q = Number.parseInt(input.value, 10);
      if (!Number.isFinite(q) || q < 1) q = 1;

      const min = Number.parseInt(input.getAttribute('min') || '1', 10);
      const maxAttr = input.getAttribute('max');
      const max = maxAttr != null ? Number.parseInt(maxAttr, 10) : Number.POSITIVE_INFINITY;

      if (Number.isFinite(min) && q < min) q = min;
      if (Number.isFinite(max) && q > max) q = max;

      return q;
    }

    /**
     * @param {number} cents
     * @returns {string}
     */
    formatMoney(cents) {
      const currency = this.dataset.currency || 'USD';
      const locale = this.dataset.locale || undefined;
      const appendCode = this.dataset.currencyDisplay === 'code';

      try {
        const formatted = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
        }).format(cents / 100);
        return appendCode ? `${formatted} ${currency}` : formatted;
      } catch {
        const fallback = (cents / 100).toFixed(2);
        return appendCode ? `${fallback} ${currency}` : fallback;
      }
    }

    /**
     * @returns {void}
     */
    sync() {
      const productInfo = this.closest('product-info');
      if (!productInfo) return;

      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      const span = productInfo.querySelector('[data-individual-atc-total]');
      if (!span) return;

      const priceCents = this.getVariantPriceCents(productInfo);
      if (priceCents == null) return;

      const quantity = this.getQuantity(productInfo, sectionId);
      const totalCents = priceCents * quantity;
      span.textContent = this.formatMoney(totalCents);
    }
  }

  customElements.define('individual-atc-total', IndividualAtcTotal);
}
